/**
 * iter87.361 — NWSL Dark Horse Pick: seed 5-8 teams only, points PER
 * WIN across the whole bracket (admin-editable via `tournament.
 * scoring.nwsl_darkhorse_win_points`). Same shape as the Champions
 * Pick modal, filtered to the bottom half of the bracket.
 * iter87.364 (user-provided brand colors) — real club color swatches
 * via `TeamSwatch sport="nwsl"` instead of a flat gray circle.
 *
 * iter87.467 (user-requested — floating Save button on every
 * predictor/Dark Horse modal) — tapping a team used to save
 * instantly; now it only drafts a selection, and a floating "Save
 * Pick" footer button (always visible) commits it.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Swords, AlertTriangle, Info, Check } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

export default function NwslDarkhorsePickModal({ open, onClose, onSaved, tournamentId, participantId, existingPick, winPoints = 5 }) {
    const [savedPick, setSavedPick] = useState(existingPick || null);
    const [draftPick, setDraftPick] = useState(existingPick || null);
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setSavedPick(existingPick || null);
        setDraftPick(existingPick || null);
        api.getNwslBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPick]);

    if (!open) return null;
    const eligibleTeams = (bracket?.teams || []).filter((t) => (t.seed || 0) >= 5).sort((a, b) => a.seed - b.seed);
    const locked = !!bracket?.pre_season_locked;
    // iter87.444 — mirrors NwslChampionPickModal.jsx's seeding-not-final banner.
    const fieldFinal = (bracket?.fixtures || []).length > 0;
    const savedTeam = savedPick ? eligibleTeams.find((t) => t.id === savedPick) : null;
    const dirty = draftPick !== savedPick;

    const onSavePick = async () => {
        if (!draftPick || locked) return;
        setSaving(true);
        try {
            await api.submitNwslDarkhorsePick(tournamentId, participantId, draftPick);
            setSavedPick(draftPick);
            onSaved?.(draftPick);
            toast.success("Dark Horse Pick saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your Dark Horse Pick");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="nwsl-darkhorse-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-volt/40 bg-obsidian max-w-lg w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-volt flex items-center justify-center shrink-0">
                        <Swords className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">Dark Horse Pick · +{winPoints}/win</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-nwsl-darkhorse-pick-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="nwsl-darkhorse-pick-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick one team seeded 5th-8th to pull off an upset run. Locks with Champions Pick at Round 1 kickoff — earns +{winPoints} pts for every playoff game your pick wins (QF + SF + Final all stack).
                </p>
            </div>

            {locked && (
                <p data-testid="nwsl-darkhorse-locked-notice" className="text-heat text-[11px] uppercase tracking-widest font-bold mb-3">
                    Locked — Round 1 has started. Your pick below is final.
                </p>
            )}

            {!locked && !fieldFinal && eligibleTeams.length > 0 && (
                <div
                    data-testid="nwsl-darkhorse-seeding-not-final-banner"
                    className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2 mb-3"
                >
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        Seeding isn't final yet — this top-8 field is projected only and can still shift before
                        the last day of the regular season. Come back to make your final pick once the season
                        ends and seeding is official.
                    </span>
                </div>
            )}

            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2" data-testid="nwsl-darkhorse-current">
                {savedTeam ? <>Saved: <span className="text-volt">{savedTeam.name}</span></> : "No pick saved yet"}
            </p>

            {eligibleTeams.length > 0 ? (
                <div className="grid grid-cols-2 gap-2" data-testid="nwsl-darkhorse-grid">
                    {eligibleTeams.map((t) => {
                        const isSelected = draftPick === t.id;
                        return (
                            <motion.button
                                key={t.id}
                                data-testid={`nwsl-darkhorse-option-${t.id}`}
                                disabled={locked}
                                onClick={() => !locked && setDraftPick(t.id)}
                                whileHover={!locked ? { y: -2, scale: 1.015 } : {}}
                                whileTap={!locked ? { scale: 0.975 } : {}}
                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                className={`p-3 rounded-lg border text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                                    isSelected ? "border-volt bg-volt/[0.08]" : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-volt/40"
                                }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <TeamSwatch name={t.name} sport="nwsl" size="w-7 h-7 text-[9px]" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold truncate flex items-center gap-1">{t.name}{isSelected && <Check className="w-3 h-3 text-volt shrink-0" />}</div>
                                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">Seed #{t.seed}</div>
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>
            ) : (
                <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid="nwsl-darkhorse-no-teams">
                    The playoff bracket isn't seeded yet — check back once the regular season wraps.
                </p>
            )}
            </div>

            <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="nwsl-darkhorse-pick-footer">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400" data-testid="nwsl-darkhorse-pick-count">
                        {dirty ? "Unsaved pick" : savedTeam ? "Saved" : "No pick yet"}
                    </span>
                    <button
                        type="button"
                        disabled={saving || !dirty || locked}
                        onClick={onSavePick}
                        data-testid="nwsl-darkhorse-save-btn"
                        className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-volt text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                    >
                        <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Pick"}
                    </button>
                </div>
            </div>
        </BentoModal>
    );
}
